/**
 * clipDownloadJobs.ts
 *
 * The files a bulk Download of the Content Studio clips grid would produce:
 * every published aspect ratio of every pick, each named by the render that
 * would actually come down.
 *
 * The grid's Download was built on `getPublishedClipVariants`, i.e. on
 * `videoFilePrimary` alone — the field only the canonical cell (original
 * language × AR × highest resolution) writes. So a clip published only in a
 * translation contributed no files and the action was absent from the bulk bar,
 * on a clip whose render is sitting in its own Downloads panel (PLAT-11660).
 *
 * This unions the two sources the same way the cards do, by calling
 * `deriveClipPublishState` rather than restating the rule — a badge that reads
 * published and a bulk bar that offers no Download would be the same bug from
 * the other side. The legacy field still answers first, so an original-language
 * publish keeps its existing Mux-backed URL and downloads exactly as it did.
 *
 * Every job carries a file: a published AR that can name neither a legacy URL
 * nor a record is dropped, because the gate and the download read this one list
 * — offering a Download that then hands over nothing is worse than its absence.
 *
 * No IO, no MobX: the caller supplies the records and resolves each record's
 * signed URL (`resolvePublishRecordVideoUrl`).
 */

import { getPublishedClipVariants } from '@src/utility/videoClip';

import { deriveClipPublishState } from './clipCardPublishState';

import type { PublishRecord } from '@src/types.ts/publishRecord';
import type { OtherVideo } from '@src/types.ts/story';

/** One file: one aspect ratio of one clip, from exactly one of the two sources. */
export interface ClipDownloadJob {
  clip: OtherVideo;
  aspectRatio: string;
  /** The legacy `videoFilePrimary` render, when it serves this aspect ratio. */
  url: string | null;
  /** The translated render, when only a `PublishRecord` holds this AR's file. */
  record: PublishRecord | null;
}

export function clipDownloadJobs(
  clips: OtherVideo[],
  recordsFor: (videoId: string) => PublishRecord[],
  originalLanguage: string,
): ClipDownloadJob[] {
  return clips.flatMap(clip => {
    // Both walks dedupe per aspect ratio over `[clip, ...associatedVideos]` in
    // the same order, so a format's legacy URL is this AR's own.
    const legacyUrls = new Map(
      getPublishedClipVariants(clip).map(variant => [
        variant.aspectRatio,
        variant.url,
      ]),
    );

    return deriveClipPublishState(clip, recordsFor, originalLanguage)
      .formats.filter(format => format.isPublished)
      .flatMap((format): ClipDownloadJob[] => {
        // `record` is set only where the legacy field cannot serve the AR, so
        // the two branches never both hold for one format.
        if (format.record) {
          return [
            {
              clip,
              aspectRatio: format.aspectRatio,
              url: null,
              record: format.record,
            },
          ];
        }
        const url = legacyUrls.get(format.aspectRatio);
        return url
          ? [{ clip, aspectRatio: format.aspectRatio, url, record: null }]
          : [];
      });
  });
}
